import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Floating "← Inbox" bar on nomad-fest.com pages opened from the inbox
 * (…?from=inbox&hub=<notification id>). Stays while navigating around until
 * the user goes back or closes it. "Erledigt" marks the notification done and
 * returns to the inbox.
 */
const KEY = 'nf_from_inbox';

type State = { hub: string | null; ts: number };

const read = (): State | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as State;
    return Date.now() - s.ts < 6 * 3600e3 ? s : null;
  } catch { return null; }
};

export function InboxReturnBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<State | null>(read);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get('from') === 'inbox') {
      const s = { hub: p.get('hub'), ts: Date.now() };
      try { sessionStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ }
      setState(s);
    }
  }, [location.search]);

  if (!state || location.pathname.startsWith('/inbox')) return null;

  const close = () => { try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } setState(null); };
  const back = () => { close(); navigate('/inbox'); };
  const done = async () => {
    if (state.hub) {
      await (supabase as any).rpc('hub_update', { p_ids: [state.hub], p_action: 'done' });
      toast.success('Als erledigt markiert');
    }
    back();
  };

  return (
    <div className="fixed z-[650] left-1/2 -translate-x-1/2 bottom-[max(env(safe-area-inset-bottom),16px)] flex items-center gap-1 rounded-full bg-teal-700 text-white shadow-lg pl-1 pr-1 py-1">
      <button onClick={back} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium hover:bg-white/10">
        <ArrowLeft className="h-4 w-4" /> Inbox
      </button>
      {state.hub && (
        <button onClick={done} className="inline-flex items-center gap-1.5 rounded-full bg-white text-teal-800 px-3 py-1.5 text-sm font-medium">
          <Check className="h-4 w-4" /> Erledigt
        </button>
      )}
      <button onClick={close} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Leiste schliessen"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

/** Open a link from the inbox: same app for nomad-fest.com (with the return bar), new window otherwise. */
export function openFromInbox(link: string, hubId: string | null, navigate: (to: string) => void) {
  if (link.startsWith('mailto:') || link.startsWith('tel:')) { window.location.href = link; return; }
  let url: URL;
  try { url = new URL(link, window.location.origin); } catch { window.open(link, '_blank'); return; }
  const sameApp = url.host === window.location.host || url.host === 'nomad-fest.com' || url.host === 'www.nomad-fest.com';
  if (!sameApp) { window.open(url.toString(), '_blank', 'noopener'); return; }
  url.searchParams.set('from', 'inbox');
  if (hubId) url.searchParams.set('hub', hubId);
  navigate(url.pathname + url.search + url.hash);
}
